import type { ButtonHTMLAttributes } from "react";

/**
 * The one button.
 *
 * Every visual decision lives in styles.css's `.btn*` classes and app.css's
 * additions — this component only decides which of them apply, and makes the
 * two things every site got wrong on its own impossible: an invisible loading
 * spinner (a primary-tinted ring drawn on the primary gradient) and a
 * `<button>` that submits a form because nobody typed `type`.
 */
export type ButtonVariant = "primary" | "secondary" | "tertiary" | "danger";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  /** Full width below 480px only — a phone idiom, never a desktop one. */
  block?: boolean;
};

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  block = false,
  disabled,
  children,
  className = "",
  type,
  ...rest
}: ButtonProps) {
  const classes = [
    "btn",
    `btn-${variant}`,
    size === "lg" ? "btn-lg" : "",
    size === "sm" ? "btn-sm" : "",
    block ? "btn-block" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <button
      // Explicit, and defaulted: an untyped <button> inside a <form> is a
      // submit button, which is how a "Show password" toggle posts the form.
      type={type ?? "button"}
      className={classes}
      disabled={disabled || loading}
      // Announces the pending state without swapping the label out, which would
      // otherwise re-trigger the screen reader on every keystroke-driven render.
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <span className="spinner" aria-hidden="true" /> : null}
      {children}
    </button>
  );
}

/**
 * A square tonal button carrying only an icon — the bell, a dialog's close, a
 * row's delete. It ALWAYS needs an `aria-label`: that is the only accessible
 * name it can have, which is why the prop is required rather than optional.
 */
export type IconButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label"> & {
  label: string;
  /** Unread count rendered as the corner dot. 0 or absent renders nothing. */
  count?: number;
};

export function IconButton({
  label,
  count,
  children,
  className = "",
  type,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type ?? "button"}
      className={`icon-btn ${className}`.trim()}
      aria-label={label}
      {...rest}
    >
      {children}
      {count && count > 0 ? (
        // Capped so the pill never outgrows the 2.5rem button it sits on.
        <span className="icon-btn__dot" aria-hidden="true">
          {count > 9 ? "9+" : count}
        </span>
      ) : null}
    </button>
  );
}
